import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { fetchEvent, thumbUrl, type EventSummary, type Photo } from "./api.js";
import { dateLabel, isDraft, useCoarsePointer } from "./shared.js";

/**
 * « Ranger dans… » : où envoyer les photos sélectionnées.
 */
const RECENT_KEY = "diapo.recentDestinations";

function readRecent(): string[] {
  try {
    const raw = localStorage.getItem(RECENT_KEY);
    const parsed = raw ? (JSON.parse(raw) as unknown) : null;
    return Array.isArray(parsed) ? parsed.filter((x): x is string => typeof x === "string") : [];
  } catch {
    return [];
  }
}

export function noteRecent(id: string): void {
  try {
    localStorage.setItem(RECENT_KEY, JSON.stringify([id, ...readRecent().filter((x) => x !== id)].slice(0, 8)));
  } catch {
    /* localStorage indisponible : la fonctionnalité dégrade sans casser */
  }
}

/**
 * Où ranger les photos sélectionnées. Remplace un menu déroulant de 84 titres :
 * on reconnaît un album à sa vignette et à sa date, on ne se souvient pas d'une
 * liste. Les destinations récemment utilisées viennent en tête — on trie par
 * rafales sur le même album.
 */
export function DestinationPicker({
  events,
  photoIds,
  onPick,
  onClose,
}: {
  events: EventSummary[];
  /** Les photos qu'on range, dans l'ordre d'affichage. */
  photoIds: string[];
  onPick: (id: string) => void;
  onClose: () => void;
}) {
  const count = photoIds.length;
  const [q, setQ] = useState("");
  const [hovered, setHovered] = useState<EventSummary | null>(null);
  const [previews, setPreviews] = useState<Record<string, Photo[]>>({});
  const timerRef = useRef<number | null>(null);
  const recent = readRecent();
  const coarse = useCoarsePointer();

  const cancelPeek = () => {
    if (timerRef.current !== null) {
      window.clearTimeout(timerRef.current);
      timerRef.current = null;
    }
  };

  /**
   * Une vignette de couverture ne dit pas si la photo va dans cet album : on charge
   * son contenu au survol. Deux règles pour que ce soit l'**intention** qui compte,
   * pas le passage du curseur :
   * - il faut s'arrêter sur une carte (temporisation) ;
   * - quitter une carte avant ce délai annule tout (`cancelPeek` au mouseleave).
   * Sans la seconde, traverser la grille pour atteindre « Ranger ici » remplaçait
   * l'aperçu par celui de la dernière carte frôlée.
   * Le contenu est mis en cache : un album n'est chargé qu'une fois.
   */
  const show = (e: EventSummary): void => {
    setHovered(e);
    if (previews[e.id]) return;
    fetchEvent(e.id)
      .then((d) => setPreviews((p) => ({ ...p, [e.id]: d.photos })))
      .catch(() => {});
  };
  const peek = (e: EventSummary) => {
    cancelPeek();
    timerRef.current = window.setTimeout(() => {
      timerRef.current = null;
      show(e);
    }, 250);
  };
  useEffect(() => cancelPeek, []);

  /**
   * Au doigt, il n'y a pas de survol : un premier appui montre le contenu de l'album,
   * et c'est « Ranger ici » qui valide. Sinon on rangerait à l'aveugle, sur la seule
   * vignette de couverture — exactement ce que l'aperçu sert à éviter.
   */
  const activate = (e: EventSummary): void => {
    if (coarse) show(e);
    else onPick(e.id);
  };

  const needle = q.trim().toLowerCase();
  // On cherche dans le titre **et** la description : le titre dit l'occasion, la description
  // dit ce qu'on y trouve, et c'est souvent elle qu'on a en tête sans se rappeler du titre.
  const matching = events.filter((e) => {
    if (!needle) return true;
    const titre = isDraft(e) ? "sans titre" : e.title.toLowerCase();
    return titre.includes(needle) || (e.description ?? "").toLowerCase().includes(needle);
  });
  const parDate = (a: EventSummary, b: EventSummary): number =>
    (a.dateYear ?? 9999) - (b.dateYear ?? 9999);

  /*
   * Deux têtes de liste, qui répondent à deux besoins différents :
   *  - « Derniers créés » : je viens de créer cet album pour ces photos, il doit être là ;
   *  - « Récemment utilisés » : je trie en rafale vers le même album.
   * Un album n'apparaît jamais deux fois. Dès qu'on filtre, tout redevient une seule liste :
   * un résultat de recherche éclaté en trois sections se lit mal.
   */
  const derniersCrees = needle
    ? []
    : [...events]
        .filter((e) => e.createdAt)
        .sort((a, b) => (b.createdAt ?? "").localeCompare(a.createdAt ?? ""))
        .slice(0, 5);
  const dejaVus = new Set(derniersCrees.map((e) => e.id));
  const recemment = needle
    ? []
    : recent
        .map((id) => events.find((e) => e.id === id))
        .filter((e): e is EventSummary => !!e && !dejaVus.has(e.id));
  for (const e of recemment) dejaVus.add(e.id);

  const rank = (e: EventSummary): number => (isDraft(e) ? 0 : 1);
  const reste = [...matching]
    .filter((e) => !dejaVus.has(e.id))
    .sort((a, b) => rank(a) - rank(b) || parDate(a, b));

  return createPortal(
    <div className="modal" data-testid="dest-picker" onClick={onClose}>
      <div className="modal__card modal__card--wide" onClick={(e) => e.stopPropagation()}>
        <header className="modal__head">
          <h3 className="modal__title">
            Ranger {count} photo{count > 1 ? "s" : ""}
          </h3>
          <button className="modal__close" onClick={onClose} aria-label="Fermer">×</button>
        </header>
        {/*
          Ce qu'on range, montré avant où le ranger. Sans ça il faut se souvenir de sa
          sélection pendant qu'on parcourt 111 albums — or c'est justement la photo qui
          dit dans lequel elle va. Seule, elle s'affiche en grand ; à plusieurs, en bande
          qui défile horizontalement dans son propre conteneur.
        */}
        <div
          className={`destsel ${count === 1 ? "destsel--solo" : ""}`}
          data-testid="dest-selection"
        >
          {photoIds.map((id) => (
            <img key={id} className="destsel__img" src={thumbUrl(id)} alt="" loading="lazy" />
          ))}
        </div>
        <input
          className="dest__search"
          placeholder="Filtrer par titre ou description…"
          value={q}
          autoFocus
          onChange={(e) => setQ(e.target.value)}
          data-testid="dest-search"
        />
        <div className={`dest__layout ${coarse && hovered ? "dest__layout--detail" : ""}`}>
          <div className="dest__cols">
            {[
              { titre: "Derniers créés", liste: derniersCrees },
              { titre: "Récemment utilisés", liste: recemment },
              { titre: needle ? "" : "Tous les albums", liste: reste },
            ]
              .filter((s) => s.liste.length > 0)
              .map((section) => (
                <div key={section.titre || "resultats"} className="dest__section">
                  {section.titre && <p className="dest__label">{section.titre}</p>}
                  <div className="dest__grid">
                    {section.liste.map((e) => (
                      <button
                        key={e.id}
                        className={`dest ${isDraft(e) ? "dest--draft" : ""} ${hovered?.id === e.id ? "dest--peek" : ""}`}
                        onClick={() => activate(e)}
                        onMouseEnter={() => peek(e)}
                        onMouseLeave={cancelPeek}
                        onFocus={() => peek(e)}
                        data-testid="dest-card"
                      >
                        <div className="dest__img">
                          {e.coverPhotoId ? (
                            <img src={thumbUrl(e.coverPhotoId)} alt="" loading="lazy" />
                          ) : (
                            <div className="card__placeholder" />
                          )}
                        </div>
                        <span className={`dest__title ${isDraft(e) ? "card__title--draft" : ""}`}>
                          {isDraft(e) ? "Sans titre" : e.title}
                        </span>
                        <span className="dest__sub">
                          {dateLabel(e) && <span className="card__date">{dateLabel(e)}</span>}
                          <span className="card__n">{e.photoCount}</span>
                        </span>
                      </button>
                    ))}
                  </div>
                </div>
              ))}
            {derniersCrees.length + recemment.length + reste.length === 0 && (
              <p className="modal__muted">Aucune destination ne correspond.</p>
            )}
          </div>

          <aside className="destprev" data-testid="dest-preview">
            {!hovered ? (
              <p className="destprev__empty">
                {coarse
                  ? "Touche un album pour voir ce qu'il contient déjà."
                  : "Survole un album pour voir ce qu'il contient déjà."}
              </p>
            ) : (
              <>
                {coarse && (
                  <button
                    className="destprev__back"
                    onClick={() => setHovered(null)}
                    data-testid="dest-back"
                  >
                    ‹ Choisir un autre album
                  </button>
                )}
                <div className="destprev__head">
                  <span className={`destprev__title ${isDraft(hovered) ? "card__title--draft" : ""}`}>
                    {isDraft(hovered) ? "Sans titre" : hovered.title}
                  </span>
                  <span className="destprev__meta">
                    {dateLabel(hovered) && <span className="card__date">{dateLabel(hovered)}</span>}
                    <span className="card__n">{hovered.photoCount} photos</span>
                  </span>
                </div>
                {previews[hovered.id] ? (
                  // Toutes les photos : la zone remplit la hauteur et défile si besoin,
                  // le bouton reste hors du défilement donc toujours visible.
                  <div className="destprev__grid" data-testid="dest-preview-grid">
                    {previews[hovered.id]!.map((ph) => (
                      <img key={ph.id} src={thumbUrl(ph.id)} alt="" loading="lazy" />
                    ))}
                  </div>
                ) : (
                  <p className="destprev__empty">Chargement…</p>
                )}
                <button className="destprev__pick" onClick={() => onPick(hovered.id)}>
                  Ranger ici
                </button>
              </>
            )}
          </aside>
        </div>
      </div>
    </div>,
    document.body,
  );
}
